import { createSlice } from '@reduxjs/toolkit';
import { calendarApiV4 as api } from 'api';
import { transformFiltersToQuery } from '../utils/transformFilterToQuery';

export const reminderSlice = createSlice({
    name: 'reminder',
    initialState: {
        pending: false,
        savePending: false,
        reminders: [],
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
        error: null,
        deletePending: false,
        deleteError: null,
        deleteId: null,
    },
    reducers: {
        start: (state, action) => {
            state.pending = true;
        },
        startSave: (state, action) => {
            state.savePending = true;
        },
        startDelete: (state, action) => {
            state.deletePending = true;
            state.deleteId = action.payload;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        stopSave: (state, action) => {
            state.savePending = false;
        },
        addReminder: (state, action) => {
            state.savePending = false;
            state.reminders = [action.payload, ...state.reminders];
        },
        editReminder: (state, action) => {
            state.savePending = false;
            const index = state.reminders.findIndex((reminder) => reminder.id === action.payload.id);
            if (index !== -1) {
                state.reminders = [
                    ...state.reminders.slice(0, index),
                    action.payload,
                    ...state.reminders.slice(index + 1),
                ];
            }
        },
        deleteReminder: (state, action) => {
            state.deletePending = false;
            const index = state.reminders.findIndex((reminder) => reminder.id === action.payload.id);
            if (index !== -1) {
                state.reminders = [...state.reminders.slice(0, index), ...state.reminders.slice(index + 1)];
            }
        },
        setReminders: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.reminders = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setError: (state, action) => {
            state.pending = false;
            state.error = action.payload;
        },
        setSaveError: (state, action) => {
            state.savePending = false;
            state.error = action.payload;
        },
    },
});

export const {
    start,
    stop,
    setReminders,
    setError,
    addReminder,
    editReminder,
    deleteReminder,
    startDelete,
    startSave,
    stopSave,
    setSaveError,
} = reminderSlice.actions;

export const getReminders = (state) => state.reminder.reminders;
export const getRemindersTotal = (state) => state.reminder.total;
export const getRemindersPending = (state) => state.reminder.pending;
export const getDeletePending = (state) => state.reminder.deletePending;
export const getDeleteId = (state) => state.reminder.deleteId;
export const getRemindersError = (state) => state.reminder.error;
export const getRemindersSavePending = (state) => state.reminder.savePending;

export const fetchRemaindersAction =
    ({ queryParams, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            let query = transformFiltersToQuery(queryParams);
            dispatch(start());
            api.get('reminder-rules' + query)
                .then((response) => {
                    dispatch(setReminders(response.data));
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export const addReminderAction =
    ({ dataDto, onSuccess, onFailure }) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            api.post('reminder-rules', dataDto)
                .then((response) => {
                    dispatch(addReminder(response.data));
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export const editReminderAction =
    ({ id, dataDto, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            dispatch(startSave());
            api.put('reminder-rules/' + id, dataDto)
                .then((response) => {
                    dispatch(editReminder(response.data));
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setSaveError(error));
                    if (onFailure) {
                        onFailure(error);
                    }
                });
        } catch (e) {
            dispatch(stopSave());
            return console.error('error', e);
        }
    };

export const deleteReminderAction =
    ({ id, onSuccess, onFailure } = {}) =>
    async (dispatch) => {
        try {
            dispatch(startDelete(id));
            api.delete('reminder-rules/' + id)
                .then((response) => {
                    dispatch(deleteReminder(response.data));
                    if (onSuccess) {
                        onSuccess();
                    }
                    return response;
                })
                .catch((error) => {
                    dispatch(setError(error));
                    if (onFailure) {
                        onFailure();
                    }
                });
        } catch (e) {
            dispatch(stop());
            return console.error('error', e);
        }
    };

export default reminderSlice.reducer;
